import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { AgentConversationNormalizedListData } from '@/pages/SessionHistory/types';
import type { HistoryMenuFacetNode, SessionHistoryItem } from './types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const ALL_FACET_KEY = '全部';
export const AGENT_CONVERSATION_PAGE_SIZE = 20;
export const LOAD_MORE_THRESHOLD_PX = 120;

const HAS_TIMEZONE_SUFFIX = /([zZ]|[+-]\d{2}:?\d{2})$/;

/** 后端无时区后缀时按 UTC 解析，再格式化为北京时间 */
export function formatBeijingDateTime(value?: string, template = 'YYYY-MM-DD HH:mm') {
  if (!value?.trim()) {
    return '';
  }
  const trimmed = value.trim();
  const parsed = HAS_TIMEZONE_SUFFIX.test(trimmed) ? dayjs(trimmed) : dayjs.utc(trimmed);
  if (!parsed.isValid()) {
    return value;
  }
  return parsed.tz('Asia/Shanghai').format(template);
}

export function buildHistoryMenuFacetTree(data?: API.AgentConversationFacetsData): HistoryMenuFacetNode[] {
  const items = data?.items ?? [];
  const total = data?.total ?? items.reduce((sum, item) => sum + (item.count || 0), 0);

  const nodes: HistoryMenuFacetNode[] = items.map((item) => {
    const family = item.agent_family || item.label || '';
    return {
      key: family,
      label: item.label || family,
      icon: item.icon,
      count: item.count || 0,
      agent_family: item.agent_family,
      children: item.children?.map((child) => {
        const agent = child.agent || child.label || '';
        return {
          key: `${family}:${agent}`,
          label: child.label || agent,
          icon: child.icon,
          count: child.count || 0,
          agent_family: item.agent_family,
          agent: child.agent,
        };
      }),
    };
  });

  return [
    {
      key: ALL_FACET_KEY,
      label: ALL_FACET_KEY,
      count: total,
    },
    ...nodes,
  ];
}

export function findFacetNodeByKey(nodes: HistoryMenuFacetNode[], key: string): HistoryMenuFacetNode | undefined {
  for (const node of nodes) {
    if (node.key === key) {
      return node;
    }
    if (node.children?.length) {
      const matched = findFacetNodeByKey(node.children, key);
      if (matched) {
        return matched;
      }
    }
  }
  return undefined;
}

export function getNavFilterParamsFromFacetNode(node?: Pick<HistoryMenuFacetNode, 'key' | 'agent_family' | 'agent'> | null): Partial<API.AgentConversationListParams> {
  if (!node || node.key === ALL_FACET_KEY || !node.agent_family) {
    return {};
  }
  if (node.agent) {
    return {
      agent_family: node.agent_family,
      agent: node.agent,
    };
  }
  return {
    agent_family: node.agent_family,
  };
}

export function flattenFacetTree(nodes: HistoryMenuFacetNode[], depth = 0): Array<HistoryMenuFacetNode & { depth: number }> {
  return nodes.flatMap((node) => [{ ...node, depth }, ...(node.children ? flattenFacetTree(node.children, depth + 1) : [])]);
}
export const compactParams = <T extends Record<string, any>>(params: T): Partial<T> => {
  return Object.entries(params).reduce<Partial<T>>((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc[key as keyof T] = value;
    }
    return acc;
  }, {});
};

export const normalizeAgentConversationCategory = (item: API.AgentConversationItem): any => {
  const family = item.agent_family?.toLowerCase();
  const agent = item.agent?.toLowerCase();

  if (family === 'seo' || family === 'seo blog') {
    return 'seo';
  }

  if (family === 'insight') {
    return 'insight';
  }

  if (family === 'muse') {
    return agent === 'reel' ? 'reel' : 'image';
  }

  // if (agent === 'reel') {
  //   return 'Reel';
  // }

  return 'muse';
};

export const formatConversationTitle = (title?: string) => {
  if (!title?.trim().startsWith('{')) return title || '';

  try {
    const parsed: unknown = JSON.parse(title);
    if (parsed && typeof parsed === 'object' && 'parts' in parsed && Array.isArray(parsed.parts)) {
      const text = parsed.parts
        .filter((part: unknown): part is { type: 'text'; text: string } => part !== null && typeof part === 'object' && 'type' in part && part.type === 'text' && 'text' in part && typeof part.text === 'string')
        .map((part) => part.text)
        .join('');
      return text || title;
    }
  } catch {
    // 普通标题可能以 { 开头，保持原样。
  }
  return title;
};

export const normalizeAgentConversationItem = (item: API.AgentConversationItem): SessionHistoryItem => {
  return {
    id: item.session_id,
    session_id: item.session_id,
    title: formatConversationTitle(item.title),
    category: normalizeAgentConversationCategory(item),
    status: item.execution_status === 'completed' ? 'ready' : 'loading',
    agent_family: item.agent_family,
    agent: item.agent,
    cover_url: item.cover_url,
    read_status: item.read_status,
    latest_stream_id: item.latest_stream_id,
    icon: item.icon,
    agent_label: item.agent_label,
    created_at: item.updated_at || item.created_at,
    updated_at: item.updated_at,
  };
};

export const normalizeAgentConversationListData = (data?: API.AgentConversationListData): AgentConversationNormalizedListData => {
  return {
    items: (data?.items || []).map(normalizeAgentConversationItem),
    total: data?.total,
    next_cursor: data?.next_cursor,
    has_more: data?.has_more,
  };
};

export function getConversationItemKey(item: Pick<SessionHistoryItem, 'id' | 'session_id'>) {
  return item.session_id || item.id;
}

/** 按 agent_family / agent（category 兜底）解析产品页路径，并以 query 携带 session_id */
export function getSessionProductPath(item: Pick<SessionHistoryItem, 'session_id' | 'id' | 'agent_family' | 'agent' | 'category'>): string | null {
  const sessionId = item.session_id || item.id;
  if (!sessionId) {
    return null;
  }

  const family = item.agent_family?.toLowerCase() || '';
  const agent = item.agent?.toLowerCase() || '';
  const category = item.category as any;

  let pathname = '';
  if (family === 'insight' || category === 'insight') {
    pathname = '/ai-analysis';
  } else if (family === 'seo' || category === 'seo') {
    pathname = '/seo-create/blog-creation';
  } else if (agent === 'reel' || category === 'reel') {
    pathname = '/generate-media/chat-reel';
  } else if (agent === 'image' || category === 'image') {
    pathname = '/generate-media/chat-image';
  }

  if (!pathname) {
    return null;
  }

  return `${pathname}?session_id=${encodeURIComponent(sessionId)}`;
}

export function mergeConversationListItems(previous: SessionHistoryItem[], next: SessionHistoryItem[]) {
  if (!next.length) {
    return previous;
  }

  const exists = new Set(previous.map(getConversationItemKey).filter(Boolean));
  const appended = next.filter((item) => {
    const key = getConversationItemKey(item);
    if (!key || exists.has(key)) {
      return false;
    }
    exists.add(key);
    return true;
  });

  return appended.length ? [...previous, ...appended] : previous;
}

export function resolveConversationCursorState(data: AgentConversationNormalizedListData) {
  const nextCursor = data.has_more && data.next_cursor ? data.next_cursor : undefined;
  return {
    nextCursor,
    hasMore: Boolean(nextCursor),
  };
}

export const isHistoryItemPending = (item: Pick<SessionHistoryItem, 'title' | 'status' | 'read_status'>) => {
  return !item.title?.trim() || item.status === 'loading' || item.read_status === 'unread';
};

export const isConversationStatusPending = (item: API.AgentConversationStatusItem) => {
  return !item.title?.trim() || item.execution_status !== 'completed' || item.read_status === 'unread';
};

export const getPendingSessionIdsFromHistoryItems = (items: SessionHistoryItem[]) => {
  return Array.from(
    new Set(
      items
        .filter(isHistoryItemPending)
        .map((item) => item.session_id || item.id)
        .filter(Boolean),
    ),
  );
};

export const getPendingSessionIdsFromStatusItems = (items: API.AgentConversationStatusItem[]) => {
  return Array.from(
    new Set(
      items
        .filter(isConversationStatusPending)
        .map((item) => item.session_id)
        .filter(Boolean),
    ),
  );
};

export const mergeConversationStatusItems = (items: SessionHistoryItem[], statusItems: API.AgentConversationStatusItem[]): SessionHistoryItem[] => {
  const statusMap = new Map(statusItems.map((item) => [item.session_id, item]));

  return items.map((item) => {
    const statusItem = statusMap.get(item.session_id || item.id);
    if (!statusItem) {
      return item;
    }

    const nextStatus: SessionHistoryItem['status'] = statusItem.execution_status === 'completed' ? 'ready' : 'loading';

    return {
      ...item,
      title: formatConversationTitle(statusItem.title ?? item.title),
      status: nextStatus,
      read_status: statusItem.read_status,
      created_at: statusItem.updated_at || item.created_at,
      updated_at: statusItem.updated_at || item.updated_at,
      icon: statusItem.icon || item.icon,
    };
  });
};
